import type {
    BillActivationStatus,
    BillCreationType,
    BillingCadence,
    CancellationReason,
    InvestmentType,
    YesNo,
} from '@fruitful/types';

import type { MembershipDetailsDto } from './membership/MembershipDetailsEntityProvider';
import type { MembershipSummaryDto } from './membership/MembershipEntityProvider';

export type {
    StripeSubscriptionSummary,
    SubscriptionPhaseSummary,
    SubscriptionProduct,
} from '@fruitful/types';

export enum AdminUserRole {
    GENERAL = 'GENERAL',
    GUIDE = 'GUIDE',
    GUIDE_LEAD = 'GUIDE_LEAD',
    ENGINEER = 'ENGINEER',
    MEMBER_OPS = 'MEMBER_OPS',
    SUPERADMIN = 'SUPERADMIN',
}

export enum GuideStatus {
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
    LEFT_COMPANY = 'LEFT_COMPANY',
}

export enum MembershipType {
    SOLO = 'Solo',
    JOINT = 'Joint',
    UNKNOWN = 'Unknown',
}

export enum OnboardingStatus {
    GUIDE_SELECTION = 'GUIDE_SELECTION',
    MEMBERSHIP = 'MEMBERSHIP',
    CHECKOUT = 'CHECKOUT',
    CONTACT_INFO = 'CONTACT_INFO',
    WELCOME_CALL = 'WELCOME_CALL',
    COMPLETED = 'COMPLETED',
    JOINT_KYC_CONTACT_INFO = 'JOINT_KYC_CONTACT_INFO',
    JOINT_COMPLETED = 'JOINT_COMPLETED',
}

export enum ExternalAccountLinkingPolicy {
    NO_LIMIT = 'NO_LIMIT',
    ONE_INSTITUTION = 'ONE_INSTITUTION',
}

export enum KYCVerificationStatus {
    UNVERIFIED = 'UNVERIFIED',
    PENDING = 'PENDING',
    PROVISIONAL = 'PROVISIONAL',
    ACCEPTED = 'ACCEPTED',
    REVIEW = 'REVIEW',
    VENDOR_ERROR = 'VENDOR_ERROR',
    REJECTED = 'REJECTED',
}

export enum EmploymentStatus {
    FULL_TIME = 'FULL_TIME',
    PART_TIME = 'PART_TIME',
    FREELANCER = 'FREELANCER',
    BUSINESS_OWNER = 'BUSINESS_OWNER',
    SEARCHING = 'SEARCHING',
    STAY_AT_HOME_PARENT = 'STAY_AT_HOME_PARENT',
    UNEMPLOYED = 'UNEMPLOYED',
    RETIRED = 'RETIRED',
}

export enum TaxFiling {
    SINGLE = 'SINGLE',
    HEAD_OF_HOUSEHOLD = 'HEAD_OF_HOUSEHOLD',
    MARRIED_FILING_JOINTLY = 'MARRIED_FILING_JOINTLY',
    MARRIED_FILING_SEPARATELY = 'MARRIED_FILING_SEPARATELY',
}

export enum InsuranceCoverage {
    LIFE_INSURANCE = 'LIFE_INSURANCE',
    LONG_TERM_DISABILITY = 'LONG_TERM_DISABILITY',
    SHORT_TERM_DISABILITY = 'SHORT_TERM_DISABILITY',
    WILL = 'WILL',
    MEDICAL_DIRECTIVE = 'MEDICAL_DIRECTIVE',
    HEALTH_INSURANCE = 'HEALTH_INSURANCE',
    NONE = 'NONE',
}

export enum AddressType {
    LEGAL = 'LEGAL',
    BILLING = 'BILLING',
    SHIPPING = 'SHIPPING',
    OTHER = 'OTHER',
}

export interface UserAddress {
    id: string;
    userId: string;
    type: AddressType;
    line1: string;
    line2?: string | null;
    city: string;
    postalCode: string;
    state?: string | null;
    countryCode: string;
}

export interface Guide {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    pronouns?: string | null;
    signUpId?: string | null;
    profileImageUrl?: string | null;
    sendbirdUserId?: string | null;
    status: GuideStatus;
    bio?: string | null;
    styles?: string | null;
    oooStart?: string | null;
    oooEnd?: string | null;
    createdAt: Date;
    updatedAt: Date | null;
}

interface StandardPermissions {
    canViewAll: boolean;
    canEditAll: boolean;
}
interface StandardDetailPermissions {
    canView: boolean;
    canEdit: boolean;
}
interface StandardGatePermissions {
    canUse: boolean;
}
interface SelectedMembershipPermissions {
    general: StandardDetailPermissions;
    pii: StandardDetailPermissions;
    accounts: StandardDetailPermissions;
    addresses: StandardDetailPermissions;
    bills: StandardDetailPermissions;
    bankOps: StandardGatePermissions;
    goals: StandardDetailPermissions;
    guidanceSessions: StandardDetailPermissions;
    cancelMembership: StandardGatePermissions;
    removeFromMembership: StandardGatePermissions;
    addPartner: StandardGatePermissions;
    notes: StandardDetailPermissions;
    investmentProfile: StandardDetailPermissions;
    changeSubscription: StandardGatePermissions;
}
interface MembershipsPermissions {
    canViewAll: boolean;
}
interface GuideMessagingPermissions extends StandardGatePermissions {
    canModerate: boolean;
}
interface GuidesPermissions extends StandardPermissions {}
interface BankOpsFunctionalityPermissions extends StandardGatePermissions {}
interface ComplianceOpsFunctionality extends StandardGatePermissions {}
interface ImOpsFunctionalityPermissions extends StandardGatePermissions {}
interface AdminUsersPermissions extends StandardPermissions {}
interface AppPermissions extends StandardGatePermissions {}
interface AIPromptsPermissions extends StandardGatePermissions {}
interface BetaTransactionFeaturesPermissions extends StandardGatePermissions {}
export interface Authorization {
    selectedMembership: SelectedMembershipPermissions;
    selectedWorkflowRun: StandardDetailPermissions;
    memberships: MembershipsPermissions;
    guideMessaging: GuideMessagingPermissions;
    workflows: StandardGatePermissions;
    guides: GuidesPermissions;
    bankOpsFunctionality: BankOpsFunctionalityPermissions;
    imOpsFunctionality: ImOpsFunctionalityPermissions;
    complianceOpsFunctionality: ComplianceOpsFunctionality;
    adminUsers: AdminUsersPermissions;
    app: AppPermissions;
    aiPrompts: AIPromptsPermissions;
    betaTransactionFeatures: BetaTransactionFeaturesPermissions;
}

export type GuideWithAdvisees = Guide & { advisees: Array<{ id: string }> };

export interface AdminUser {
    id: string | null;
    email: string;
    role: AdminUserRole;
    stytchMemberId: string;
    guide: GuideWithAdvisees | null;
    guideId: string | null;
    memberId: string | null;
    createdAt: string;
    updatedAt: string;

    // Client transposition
    authorization: Authorization;
}

/**
 * Admin API / Prisma often stores completion as an ISO timestamp; member-app
 * may use booleans. Null means not completed.
 */
export type MemberJourneyMilestoneValue = boolean | string | null;

export interface MemberJourneyLog {
    id?: string;
    userId?: string;
    membershipId?: string;
    createdAt?: string;
    updatedAt?: string;
    completedSignupMoneyMapChat?: MemberJourneyMilestoneValue;
    completedGatedOnboarding?: MemberJourneyMilestoneValue;
    viewedLinkMoreAccounts?: MemberJourneyMilestoneValue;
    bookedDiscoverySession?: MemberJourneyMilestoneValue;
    completedDiscoverySession?: MemberJourneyMilestoneValue;
    bookedRoadmapSession?: MemberJourneyMilestoneValue;
    completedRoadmapSession?: MemberJourneyMilestoneValue;
    completedMoneyMap?: MemberJourneyMilestoneValue;
    moneyMapFinalizedForAction?: MemberJourneyMilestoneValue;
    /** Deprecated - feature no longer exists */
    completedWelcomeVideo?: MemberJourneyMilestoneValue;
}

export interface UserEvent {
    id: string;
    userId: string;
    eventType: string;
    metadata: Record<string, unknown> | null;
    ipAddress?: string | null;
    userAgent?: string | null;
    createdAt: string;
    updatedAt: string | null;
}

export const isMemberJourneyMilestoneComplete = (
    value: MemberJourneyMilestoneValue | undefined,
): boolean => {
    if (value === true) {
        return true;
    }
    if (typeof value === 'string' && value.length > 0) {
        return true;
    }
    return false;
};

/**
 * This is the User entity of the Member App
 */
export interface Member {
    id: string;
    membershipId: string | null;
    stytchId: string;
    syncteraPersonId: string;
    stripeCustomerId: string | null;
    email: string;
    firstName: string | null;
    lastName: string | null;
    legalFirstName: string | null;
    legalLastName: string | null;
    phoneNumber: string | null;
    dateOfBirth: string | null; // formatted YYYY-MM-DD
    isEmailVerified: boolean;
    isSubscriptionActive: boolean | null;
    disableMfa: boolean | null;
    syncteraKycStatus: KYCVerificationStatus | null;
    onboardingStatus: OnboardingStatus;
    createdAt: string;
    updatedAt: string;
    sendbirdUserId: string | null;
    externalAccountLinkingPolicy: ExternalAccountLinkingPolicy;
    depositoryAccountLimit: number;
    cancellationDate: string | null;
    cancellationReason: CancellationReason | null;
    cancellationNotes: string | null;
    quilttProfileId: string | null;
    pronouns: string | null;
    taxFiling: TaxFiling | null;
    employmentStatus: EmploymentStatus | null;
    insuranceCoverage: InsuranceCoverage[];
    investments: InvestmentType[];
    userAddress: UserAddress | null;
    pinwheelEndUserId: string | null;
    category: UserCategory;
    adminUser: AdminUser | null;
    spinwheelOverride: boolean | null;
    memberJourneyLog?: MemberJourneyLog | null;
    events?: UserEvent[] | null;
}

export enum UserCategory {
    MEMBER = 'MEMBER',
    FRUITFUL_EMPLOYEE = 'FRUITFUL_EMPLOYEE',
    FRUITFUL_EMPLOYEE_PARTNER = 'FRUITFUL_EMPLOYEE_PARTNER',
    TESTING = 'TESTING',
    FRUITFUL_VENDOR = 'FRUITFUL_VENDOR',
    VIP = 'VIP',
    B2B = 'B2B',
    EXCLUDE_SUSPICIOUS = 'EXCLUDE_SUSPICIOUS',
}

export interface GoalCategory {
    id: string;
    description: string;
    createdAt: string;
    updatedAt?: string;
}

export interface BillTransaction {
    transactionId: string;
    dateAdded: string;
    dateChanged: string;
    addedBy: string;
    hiddenBy?: string;
    status: string;
    transactionAmount: number;
    transactionDate: string;
    vendor: string;
    accountMask?: string;
    institutionName?: string;
}

export interface Bill {
    id: string;
    aiBillId?: string;
    membershipId: string;
    accountId: string;
    userId?: string;
    name?: string;
    displayName?: string;
    payee: string;
    amount: number;
    monthlyAmount: number; // Derived on client side
    category?: string;
    description?: string;
    cadence: BillingCadence;
    timingLabel: string;
    timingDescription?: string;
    creationType: BillCreationType;
    createdAt: string;
    activationStatus: BillActivationStatus;
    removedAt?: string;
    lastPaidAt?: string;
    transactions?: BillTransaction[];
}

interface SendbirdMember {
    userId?: string;
    nickname?: string;
}

// We currently return the full channel payload from backend and its typing comes from the Sendbird server sdk. The type declared here is just a cherry-picked list of fields to consume.
export interface SendbirdGroupChannel {
    channelUrl?: string;
    members?: SendbirdMember[];
}

export enum UserType {
    PRIMARY_MEMBER = 'PRIMARY_MEMBER',
    PARTNER_MEMBER = 'PARTNER_MEMBER',
    JOINT_ACCOUNT_HOLDER = 'JOINT_ACCOUNT_HOLDER',
    PROSPECT = 'PROSPECT',
}

interface TransposedMembershipFields {
    // Client transposition
    membershipType: MembershipType;
    isMembershipActive: boolean;
    membersName: string;
    membersEmail: string;
    membersPhone: string;
    membersId: string;
    membersState: string;
}

export type TransposedMembershipDetailsFields = TransposedMembershipFields & {
    // Client transposition
    users: MemberDetails[];
};

interface TransposedMemberFields {
    // Client transposition
    type: UserType;
}

interface TransposedGuidanceSessionFields {
    previousSession: string;
    nextSession: string;
}

export type MembershipSummary = MembershipSummaryDto &
    TransposedMembershipFields &
    TransposedGuidanceSessionFields & {
        hasIM: YesNo;
        memberJourneyStatus: string;
    };

export type MembershipDetails = Omit<MembershipDetailsDto, 'users'> &
    TransposedMembershipDetailsFields;

export type MemberDetails = Member & TransposedMemberFields;

interface TransposedGuideFields {
    fullName: string;
}

export type GuideDetails = Guide & TransposedGuideFields;

export enum PaycheckRoutingConfigStatus {
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
    DELETED = 'DELETED',
}

export type PaycheckStatus = PaycheckRoutingConfigStatus | 'ELIGIBLE';

export enum PaycheckRoutingRuleType {
    FIXED = 'FIXED',
    PERCENTAGE = 'PERCENTAGE',
    MINIMUM_BALANCE = 'MINIMUM_BALANCE',
}

export interface PaycheckRoutingRule {
    id: string;
    paycheckRoutingConfigId: string;
    accountId: string;
    type: PaycheckRoutingRuleType;
    amount: number;
    order?: number | null;
    isActive: boolean;
    createdAt: Date;
    updatedAt: Date | null;
}

export interface PaycheckRoutingConfig {
    id: string;
    accountId: string;
    originator: string;
    isActive: boolean;
    createdAt: Date;
    updatedAt: Date | null;
    rules?: PaycheckRoutingRule[] | null;
    receiver?: string | null;
    status: PaycheckRoutingConfigStatus;
}

export interface Paycheck {
    amount: number;
    originator: string;
    receiver: string;
    rawDescription: string;
    accountId: string;
    date: Date;
}

export interface DirectDepositSwitch {
    userId: string;
    linkTokenId: string;
    action: string;
    allocationType: string | null;
    allocationValue: number | null;
    accountName: string | null;
    lastFourAccountNumber: string | null;
    platformName: string | null;
    outcome: string;
    errorCode: string | null;
    errorType: string | null;
    createdAt: Date | null;
}

export interface TransactionStats {
    data: MonthlyMoneyMinuteData;
    month: string;
    year: string;
    bills: MonthlyStats;
    spending: SpendingStats;
}

export interface MonthlyMoneyMinuteData {
    current: CurrentTotals;
}

export interface CurrentTotals {
    income: number;
    savingsContributions: number;
    recurring: number;
}

export interface SpendingStats {
    monthlyStats: MonthlyStats[];
}

export interface MonthlyStats {
    total: number;
}

export type TransformedPaycheckRoutingConfig = Omit<
    PaycheckRoutingConfig,
    'rules'
> & {
    rules: { primary: PaycheckRoutingRule[]; remaining: PaycheckRoutingRule[] };
};

export interface PaycheckRoutingRow {
    originator: string;
    receiver: string;
    institution: string;
    account: string;
    amount: string;
    status: PaycheckStatus;
    rules: {
        primary: PaycheckRoutingRule[];
        remaining: PaycheckRoutingRule[];
    } | null;
}

export interface IncomeSplitHistoryRow {
    date: string;
    account: string;
    amount: string;
    description: string;
    emailCopy: string;
}

export interface IncomeSplitExecutionTransaction {
    id: string;
    transactionUuid: string | null;
    membershipId: string;
    sourceAccountId: string;
    destinationAccountId: string;
    sourceAccountDisplayName: string;
    destinationAccountDisplayName: string;
    amount: number;
    status: string;
    transferType: string;
    failReason: string | null;
    ruleId: string;
    originalRuleId: string;
    dateAdded: string;
    createdAt: string;
    updatedAt: string;
}

export interface IncomeSplitExecutionEmail {
    id: string;
    membershipId: string;
    incomeSplitExecutionId: string;
    transactionDate: string;
    transactionAmount: number;
    transactionDescription: string;
    transactionUuid: string;
    accountId: string;
    emailCopy: string;
    createdAt: string;
    updatedAt: string | null;
}

export interface IncomeSplitExecutionPlanTransaction {
    memo: string;
    type: string;
    order: number;
    amount: number;
    ruleId: string;
    configId: string;
    nickname: string;
    actualAmount: number;
    accountNumber: string;
    desiredAmount: number;
    originalRuleId: string;
    clientIpAddress: string;
    sourceAccountId: string;
    targetAccountId: string;
    syncteraPersonId: string;
    yearlyContributionMet: boolean;
    monthlyContributionMet: boolean;
    incomeSplitRuleAccountType: string;
}

export interface IncomeSplitExecutionPlanValidationResult {
    breaking?: boolean;
    invalidRules?: unknown[];
    triggerEmail?: boolean;
    validationErrors?: string[];
    ruleValidationDetails?: unknown[];
}

export interface IncomeSplitExecutionPlan {
    paycheckTransactions: IncomeSplitExecutionPlanTransaction[];
    minimumBalanceAlreadyMets: unknown[];
    validationResult?: IncomeSplitExecutionPlanValidationResult;
}

export interface IncomeSplitExecutionAccountState {
    accountId: string;
    status: string;
    balance: number;
    transactions: unknown[];
}

export interface IncomeSplitExecution {
    id: string;
    membershipId: string;
    incomeSplitConfigId: string;
    sourceSyncteraAccountId: string;
    sourceAccountUserId: string;
    syncteraPersonId: string;
    amount: number;
    transactionDescription: string;
    transactionUuid: string;
    webhookPostedAt: string;
    processStartedAt: string;
    processEndedAt: string | null;
    createdAt: string;
    updatedAt: string;
    accountStates: Record<string, IncomeSplitExecutionAccountState>;
    plan: IncomeSplitExecutionPlan;
    executionStatus: string;
    totalPlannedAmount: number | null;
    totalExecutedAmount: number | null;
    successfulTransfers: number;
    failedTransfers: number;
    errorMessage: {
        name: string;
        stack: string;
        message: string;
        timestamp: string;
    } | null;
    transactions: IncomeSplitExecutionTransaction[];
    emails: IncomeSplitExecutionEmail[];
}

export interface IncomeSplitExecutionRow {
    id: string;
    date: string;
    transactionDate: string;
    account: string;
    amount: string;
    description: string;
    status: string;
    successfulTransfers: number;
    failedTransfers: number;
    execution: IncomeSplitExecution;
}
